import { Table } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { selectedToData } from '../../../utils/selectedToData';
import { useClassesTable } from '../../Classes/hooks/useClassesTable';
import { ClassesTableType } from '../../Classes/types/ClassesTypes';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';
import { useTimetableStore } from '../../TimetableGrid/TimetableProvider';
import { useTimetableClasses } from '../hooks/useTimetableClasses';

export const TimetableVersionImportClasses = observer(
  ({ versionUuid, onClose }: { versionUuid: string; onClose: () => void }) => {
    const { t } = useTranslation();
    const store = useTimetableStore();

    const [loading, setLoading] = useState<boolean>(false);

    const versionClassesUuids: string[] = Array.from(store.classes.keys());

    const { importClasses } = useTimetableClasses(versionUuid);

    const { classesData } = useLoadBasicData({ pause: !versionUuid });

    const { data, columns, tableSelection, tableSorting } = useClassesTable({
      context: 'timetable-version-import',
      columns: ['name', 'shortName', 'grade'],
    });

    const delta = data.filter((c) => !versionClassesUuids.includes(c.uuid));

    return (
      <>
        <Table<ClassesTableType>
          loading={loading}
          data={delta}
          columns={columns}
          {...tableSelection}
          {...tableSorting}
          showSelected
          selectOnClick
          showActionBar
          actionBarSettings={{
            showSelectedRowsCount: true,
          }}
        />

        <ModalBottomButtons
          closeButton={{
            text: t('common.cancel'),
            callback: () => {
              onClose();
            },
          }}
          submitButton={{
            disabled: tableSelection.rowSelection ? Object.keys(tableSelection.rowSelection).length === 0 : true,
            text: t('common.import'),
            callback: async () => {
              setLoading(true);
              const selectedClasses = selectedToData<ClassesTableType>(tableSelection.rowSelection, delta);
              if (selectedClasses.length > 0) {
                await importClasses(selectedClasses.map((c) => c.uuid));
                store.addClasses(
                  classesData.classes.filter((c) => selectedClasses.map((sc) => sc.uuid).includes(c.uuid)) ?? [],
                );
              }
              setLoading(false);
              onClose();
            },
          }}
          isLoading={loading}
        />
      </>
    );
  },
);
